'use client'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'

import { gql, useMutation, useQuery } from '@apollo/client'
import {
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { SheetScrollableContent } from '@/components/ui/sheet-scrollable-content'
import {
  CREATE_FABRIC_GROUP,
  DELETE_FABRIC_GROUP,
  GET_ALL_FABRIC_FEATURES,
  GET_FABRIC_GROUP,
  UPDATE_FABRIC_GROUP,
} from './fabricQueries'
import LanguageSwitcher from '@/components/shared/languageSelect'
import { useLanguage } from '@/context/LanguageContext'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { DraggableFabricItem } from './components/DraggableFabricItem'
import { DraggableFabricList } from './components/DraggableFabricList'
import { Checkbox } from '@/components/ui/checkbox'
import '@/components/styles/Form.css' // Import the CSS file
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import FabricTypeSelect from '@/components/shared/inputComponents/FabricTypeSelect'
import ManufacturerSelect from '@/components/shared/inputComponents/ManufacturerSelect'
import { Icons } from '@/components/shared/icons'
import { useConfirmation } from '@/context/ConfirmationContext'

const formSchema = z.object({
  code: z.string().optional(),
  type: z.string(),
  manufacturer: z.number(),
  fabrics: z.array(
    z.object({
      code: z.string(),
      color_name: z.string(),
      image: z.object({
        src: z.string(),
      }),
    })
  ),
  fabric_group_profiles: z
    .array(
      z.object({
        id: z.number().optional(),
        name: z.string().optional(),
        description: z.string().optional(),
        language: z.string(),
      })
    )
    .transform((items, ctx) => {
      const filteredItems = items.filter(item => item.name?.trim() !== '')
      if (filteredItems.length === 0) {
        items.forEach((item, index) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one language profile needs to be filled',
            path: [`[${index}].name`],
          })
        })
      }
      return filteredItems
    }),
  water_repelant: z.boolean().optional(),
  virus_protection: z.boolean().optional(),
  non_flammable: z.boolean().optional(),
  pet_friendly: z.boolean().optional(),
  environment_friendly: z.boolean().optional(),
  easy_water_cleaning: z.boolean().optional(),
  fabric_feature_ids: z.array(z.number()).optional(),
})

interface MyFormProps {
  data: {
    id: number
    // Add other properties of data as needed
  }
  onDirtyChange?: (dirty: boolean) => void
  onRequestClose?: () => void // Add this prop
}

export default function FabricGroupForm({
  data,
  onDirtyChange,
  onRequestClose,
}: MyFormProps) {
  const { allLanguages, languageId } = useLanguage()
  const [id, setId] = useState(data?.id) // Introduce id state
  const { showConfirmation } = useConfirmation()

  const {
    data: queryData,
    loading: loadingData,
    error,
  } = useQuery(GET_FABRIC_GROUP, {
    variables: {
      id: id,
    },
    fetchPolicy: 'network-only',
    skip: !id,
  })

  const fabricGroupData = queryData?.findUniqueFabricGroup

  const { data: allFeaturesData } = useQuery(GET_ALL_FABRIC_FEATURES)
  const allFabricFeatures: any[] = allFeaturesData?.findManyFabricFeature ?? []

  const LEGACY_FEATURE_CODES = [
    'water_repelant',
    'virus_protection',
    'non_flammable',
    'pet_friendly',
    'environment_friendly',
    'easy_water_cleaning',
  ]

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      fabric_group_profiles: [],
      code: '',
      type: undefined,
      fabrics: [],
      manufacturer: undefined,
      water_repelant: false,
      virus_protection: false,
      non_flammable: false,
      pet_friendly: false,
      environment_friendly: false,
      easy_water_cleaning: false,
      fabric_feature_ids: [],
    },
  })

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = form

  const [updateFabricGroup] = useMutation(UPDATE_FABRIC_GROUP, {
    onError: e => {
      toast.error('Failed to update fabric group')
    },
    onCompleted: e => {
      toast.success('Fabric group updated successfully')
    },
    refetchQueries: ['GET_FABRIC_GROUPS', 'GET_FABRIC_GROUP'],
  })

  const [createFabricGroup] = useMutation(CREATE_FABRIC_GROUP, {
    onError: e => {
      toast.error('Failed to create fabric group')
    },
    onCompleted: response => {
      // Capture new ID
      toast.success('Fabric group created successfully')
      const newId = response.createNewFabricGroup.id // Adjust according to actual response structure
      setId(newId) // Set the new id
    },
    refetchQueries: ['GET_FABRIC_GROUPS'],
  })

  const [deleteFabricGroup] = useMutation(DELETE_FABRIC_GROUP, {
    refetchQueries: ['GET_FABRIC_GROUPS', 'GET_FABRIC_GROUP'],
  })

  const handleDelete = () => {
    if (id) {
      const fabricCount = fabricGroupData?.fabrics?.length || 0
      showConfirmation({
        onConfirm: async () => {
          try {
            await deleteFabricGroup({
              variables: { where: { id } },
            })
            toast.success('Fabric group deleted successfully')
            onRequestClose?.()
          } catch (error) {
            toast.error('Failed to delete fabric group')
          }
        },
        description: fabricCount
          ? `This fabric group contains ${fabricCount} fabric(s). Deleting it will also delete all its fabrics. This action cannot be undone.`
          : 'Are you sure you want to delete this fabric group? This action cannot be undone.',
        confirmText: 'Delete',
        cancelText: 'Cancel',
      })
    }
  }

  function onSubmit(values: z.infer<typeof formSchema>) {
    if (id) {
      // Use id state
      updateFabricGroup({
        variables: {
          where: { id: fabricGroupData.id },
          fabric_group_profiles: values.fabric_group_profiles,
          code: values.code,
          fabrics: values.fabrics
            .sort((a: any, b: any) => a.order - b.order)
            .map(f => ({
              code: f.code,
              color_name: f.color_name,
              src: f.image.src,
            })),
          manufacturer: values.manufacturer,
          fabric_type: values.type,
          water_repelant: values.water_repelant,
          virus_protection: values.virus_protection,
          non_flammable: values.non_flammable,
          pet_friendly: values.pet_friendly,
          environment_friendly: values.environment_friendly,
          easy_water_cleaning: values.easy_water_cleaning,
          fabric_feature_ids: values.fabric_feature_ids,
        },
      })
    } else {
      createFabricGroup({
        variables: {
          fabric_group_profiles: values.fabric_group_profiles.map(p => ({
            name: p.name,
            description: p.description,
            language: p.language,
          })),
          code: values.code,
          fabrics: values.fabrics
            .sort((a: any, b: any) => a.order - b.order)
            .map(f => ({
              code: f.code,
              color_name: f.color_name,
              src: f.image.src,
            })),
          manufacturer: values.manufacturer,
          fabric_type: values.type,
          water_repelant: values.water_repelant,
          virus_protection: values.virus_protection,
          non_flammable: values.non_flammable,
          pet_friendly: values.pet_friendly,
          environment_friendly: values.environment_friendly,
          easy_water_cleaning: values.easy_water_cleaning,
          fabric_feature_ids: values.fabric_feature_ids,
        },
      })
    }
  }

  useEffect(() => {
    if (!loadingData) {
      if (fabricGroupData && fabricGroupData.fabrics) {
        // Reset with existing data, ensuring all languages have profiles
        const existingProfiles =
          fabricGroupData.fabric_group_profiles?.filter(
            p => p.language && allLanguages.includes(p.language)
          ) ?? []

        // Create profiles for all languages, using existing data where available
        const allProfiles = allLanguages.map(lang => {
          const existingProfile = existingProfiles.find(
            p => p.language === lang
          )
          if (existingProfile) {
            return {
              id: existingProfile.id,
              name: existingProfile.name ?? '',
              description: existingProfile.description ?? '',
              language: existingProfile.language,
            }
          } else {
            // Create new profile for missing language
            return {
              name: '',
              description: '',
              language: lang,
            }
          }
        })

        const fabrics = fabricGroupData.fabrics.map(f => ({
          id: f.id.toString(), // Convert ID to string
          code: f.code,
          color_name: f.color_name,
          image: { src: f.image.src },
          order: f.order,
        }))

        reset({
          fabric_group_profiles: allProfiles,
          code: fabricGroupData.code,
          type: fabricGroupData.type,
          manufacturer: fabricGroupData.manufacturer.id,
          fabrics: fabrics.sort((a, b) => a.order - b.order),
          water_repelant: fabricGroupData.water_repelant,
          virus_protection: fabricGroupData.virus_protection,
          non_flammable: fabricGroupData.non_flammable,
          pet_friendly: fabricGroupData.pet_friendly,
          environment_friendly: fabricGroupData.environment_friendly,
          easy_water_cleaning: fabricGroupData.easy_water_cleaning,
          fabric_feature_ids: fabricGroupData.fabric_features?.map((ff: any) => ff.fabric_feature.id) || [],
        })
      } else {
        // For NEW forms, initialize with profiles for all languages
        reset({
          fabric_group_profiles: allLanguages.map(lang => ({
            name: '',
            description: '',
            language: lang,
          })),
          code: '',
          type: undefined,
          fabrics: [],
          manufacturer: undefined,
          water_repelant: false,
          virus_protection: false,
          non_flammable: false,
          pet_friendly: false,
          environment_friendly: false,
          easy_water_cleaning: false,
          fabric_feature_ids: [],
        })
      }
    }
  }, [fabricGroupData, loadingData, allLanguages, reset])

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(form.formState.isDirty)
    }
  }, [form.formState.isDirty, onDirtyChange])

  return (
    <>
      <SheetContent className="sheet-content flex flex-col">
        <SheetHeader className="flex-shrink-0">
          <div className="flex justify-between items-center">
            <SheetTitle>Fabric Group</SheetTitle>
            <LanguageSwitcher />
          </div>
          {/* <SheetDescription></SheetDescription> */}
        </SheetHeader>
        <SheetScrollableContent>
          <Form {...form}>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="space-y-4 max-w-3xl mx-auto py-5"
            >
              <div className="grid gap-4 py-4">
                <InputFieldContainer
                  label="Fabric Name"
                  description="This is your public display name."
                  control={control}
                  name={`fabric_group_profiles.${languageId}.name`}
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <Input
                          placeholder="Fabric Name"
                          type="text"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <InputFieldContainer
                  label="Description"
                  control={control}
                  name={`fabric_group_profiles.${languageId}.description`}
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <Textarea
                          placeholder="Description"
                          className="resize-none"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <DraggableFabricList
                  fabrics={form.watch('fabrics')}
                  control={control}
                  setValue={form.setValue}
                />

                <InputFieldContainer
                  label="Fabric Code"
                  description="Optional."
                  control={control}
                  name="code"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <Input placeholder="" type="text" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <InputFieldContainer
                  label="Fabric Type"
                  control={control}
                  name="type"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <FabricTypeSelect field={field} />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <InputFieldContainer
                  label="Manufacturer"
                  control={control}
                  name="manufacturer"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <ManufacturerSelect field={field} />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {allFabricFeatures.filter(f => !LEGACY_FEATURE_CODES.includes(f.code)).length > 0 && (
                  <div className="grid grid-cols-4 items-start gap-4">
                    <FormLabel className="text-right pt-3">
                      Additional Features
                    </FormLabel>
                    <div className="col-span-3 border rounded-lg bg-white">
                      <Controller
                        control={control}
                        name="fabric_feature_ids"
                        render={({ field }) => {
                          const selectedIds: number[] = field.value ?? []
                          const nonLegacyFeatures = allFabricFeatures.filter(
                            f => !LEGACY_FEATURE_CODES.includes(f.code)
                          )

                          // Build ordered group list: named groups first, ungrouped last
                          const groupMap = new Map<number | null, { groupLabel: string | null; features: any[] }>()
                          const groupOrder: (number | null)[] = []

                          nonLegacyFeatures.forEach(feature => {
                            const gid: number | null = feature.fabricFeatureGroupId ?? null
                            if (!groupMap.has(gid)) {
                              groupOrder.push(gid)
                              let groupLabel: string | null = null
                              if (feature.fabric_feature_group) {
                                const gProfile =
                                  feature.fabric_feature_group.fabric_feature_group_profiles?.find(
                                    (p: any) => p.language === allLanguages[languageId]
                                  ) ?? feature.fabric_feature_group.fabric_feature_group_profiles?.[0]
                                groupLabel = gProfile?.name ?? null
                              }
                              groupMap.set(gid, { groupLabel, features: [] })
                            }
                            groupMap.get(gid)!.features.push(feature)
                          })

                          // Move null (ungrouped) to the end
                          const sortedGroupOrder = [
                            ...groupOrder.filter(gid => gid !== null),
                            ...groupOrder.filter(gid => gid === null),
                          ]

                          const renderFeatureRow = (feature: any) => {
                            const profile =
                              feature.fabric_feature_profiles?.find(
                                (p: any) => p.language === allLanguages[languageId]
                              ) ?? feature.fabric_feature_profiles?.[0]
                            const label = profile?.name ?? feature.code
                            const isChecked = selectedIds.includes(feature.id)
                            return (
                              <div
                                key={feature.id}
                                className="flex flex-row items-center space-x-2 rounded-md p-2 ml-4"
                              >
                                <Checkbox
                                  checked={isChecked}
                                  onCheckedChange={checked => {
                                    if (checked) {
                                      field.onChange([...selectedIds, feature.id])
                                    } else {
                                      field.onChange(selectedIds.filter(id => id !== feature.id))
                                    }
                                  }}
                                />
                                <label className="flex items-center space-x-2 text-sm font-medium leading-none cursor-pointer">
                                  {feature.photo?.src_xs && (
                                    <img
                                      src={feature.photo.src_xs}
                                      alt={label}
                                      className="w-10 h-10 p-1"
                                    />
                                  )}
                                  <span>{label}</span>
                                </label>
                              </div>
                            )
                          }

                          return (
                            <>
                              {sortedGroupOrder.map(gid => {
                                const group = groupMap.get(gid)!
                                const headerLabel = gid === null ? 'Ungrouped' : (group.groupLabel ?? `Group ${gid}`)
                                return (
                                  <div key={gid ?? 'ungrouped'}>
                                    <div className="font-semibold text-sm bg-muted/50 px-2 py-1.5">
                                      {headerLabel}
                                    </div>
                                    {group.features.map(renderFeatureRow)}
                                  </div>
                                )
                              })}
                            </>
                          )
                        }}
                      />
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  {id && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={handleDelete}
                      className="shrink-0"
                    >
                      <Icons.trash className="h-4 w-4 text-red-500" />
                    </Button>
                  )}
                  <Button
                    type="submit"
                    disabled={loadingData}
                    className="flex-1"
                  >
                    {id ? 'Update' : 'Create'}
                  </Button>
                </div>
              </div>
            </form>
          </Form>
        </SheetScrollableContent>
      </SheetContent>
    </>
  )
}
